import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import {
  StartJourneyRequest,
  StartJourneyResponse,
  IntakeAnswersRequest,
  IntakeAnswersResponse,
  NotifyHomeownerRequest,
  NotifyHomeownerResponse,
  MediaUploadResponse,
  MediaType,
  Lead,
  ConsultationAvailabilityResponse,
  BookConsultationRequest,
  BookConsultationResponse,
  JourneyStatusResponse,
  FeasibilityQuestion,
} from '../models/lead.model';

@Injectable({ providedIn: 'root' })
export class LeadService {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);

  // STEP 1 — create homeowner + property + lead in one call
  start(data: StartJourneyRequest): Observable<StartJourneyResponse> {
    return this.api.post<StartJourneyResponse>('/homeowners/start', data).pipe(
      tap(res => this.auth.setSessionToken(res.sessionToken)),
    );
  }

  // STEP 2 & 3 — upsert intake answers (call multiple times; answers accumulate)
  submitIntakeAnswers(data: IntakeAnswersRequest): Observable<IntakeAnswersResponse> {
    return this.api.post<IntakeAnswersResponse>('/homeowners/intake', data);
  }

  /** Real question-bank data (FeasibilityQuestion) — replaces the hardcoded option
   *  lists that used to live in property-intake.model.ts and drift out of sync
   *  with what's actually seeded in the database. Public endpoint, no session needed. */
  getFeasibilityQuestions(category = 'INTAKE', step?: string): Observable<FeasibilityQuestion[]> {
    const params: Record<string, string> = { category };
    if (step) params['step'] = step;
    return this.api.get<FeasibilityQuestion[]>('/public/feasibility-questions', params);
  }

  notifyHomeowner(data: NotifyHomeownerRequest): Observable<NotifyHomeownerResponse> {
    return this.api.post<NotifyHomeownerResponse>('/homeowners/notify', data);
  }

  // STEP 4 — upload a single photo or document
  uploadMedia(
    propertyId: string,
    file: File,
    mediaType: MediaType,
    photoSlot?: string,
  ): Observable<MediaUploadResponse> {
    const formData = new FormData();
    // mediaType/photoSlot must be appended before file — multer parses multipart
    // fields in stream order, and its fileFilter reads req.body.mediaType while
    // the file part is still being parsed.
    formData.append('mediaType', mediaType);
    if (photoSlot) formData.append('photoSlot', photoSlot);
    formData.append('file', file);
    return this.api.postFormData<MediaUploadResponse>(
      `/homeowners/properties/${propertyId}/media`,
      formData,
    );
  }

  clearSession(): void {
    this.auth.clearSessionToken();
  }

  // ── Current homeowner / property (session-scoped) ────────────────────────
  getCurrentLead(): Observable<Lead> {
    return this.api.get<Lead>('/homeowners/me');
  }

  // ── Consultation booking ──────────────────────────────────────────────────
  getConsultationAvailability(month: string): Observable<ConsultationAvailabilityResponse> {
    return this.api.get<ConsultationAvailabilityResponse>('/homeowners/consultations/availability', { month });
  }

  bookConsultation(data: BookConsultationRequest): Observable<BookConsultationResponse> {
    return this.api.post<BookConsultationResponse>('/homeowners/consultations', data);
  }

  // ── ADU journey status ────────────────────────────────────────────────────
  getJourneyStatus(): Observable<JourneyStatusResponse> {
    return this.api.get<JourneyStatusResponse>('/homeowners/journey');
  }
}
